import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService, DroplistItem, DroplistWidget } from '@eforall/dashboard';
import { EventTypeId } from '../../../../../../../interfaces';
import { ReadonlyAccessWidget } from '../../../../../../../ui/widget';
import { AcceleratorAreaService } from '../../../../accelerator-area.service';

@Component({
	selector: 'final-presentation-set-event-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		ReadonlyAccessWidget,
		DroplistWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './set-event.dialog.html',
})
export class FinalPresentationSetEventDialog implements OnInit {
	public dialog: DialogContext<{ accVentureId: number, finalEventId?: number }> = inject(MAT_DIALOG_DATA);
	public dialogRef = inject(MatDialogRef<FinalPresentationSetEventDialog>);
	public accAreaService = inject(AcceleratorAreaService);
	public util = inject(UtilityService);
	public dialogService = inject(DialogService);

	public selectedEventId = signal<number | undefined>(undefined);

	saveActionEnabled = signal(false);

	saveAction: DialogAction = {
		id: 'save',
		cssClass: signal(undefined),
		enabled: this.saveActionEnabled,
		visible: signal(true),
		text: signal('Save'),
		linkType: 'callback',
		callback: async () => this.setFinalEventId(),
		willCloseDialog: true,
	};

	cancelAction: DialogAction = {
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: "no-action",
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.saveAction, this.cancelAction];

	icon = signal('fa-regular fa-hand-pointer');

	public isReadonly = computed(() => !this.accAreaService.canEdit());

	public selectedItem = signal<DroplistItem | undefined>(undefined);
	public readonly events = computed<DroplistItem[]>(() => {
		const finalPresentationEvents = this.accAreaService.data()?.events.filter(e => e.eventTypeId == EventTypeId.AccFinalPitch) ?? [];


		const list: DroplistItem[] = finalPresentationEvents.map((event, i) => {
			return {
				uniqueId: event.eventId,
				iconContext: {
					iconClass: 'fa-calendar',
					iconStyle: 'fa-solid',
					text: `${i + 1} - ` + this.util.date.formatUTC(event.startUTC, 'DOW MMM D, YYYY', 'H:MM AM EST', 'en-US'),
					hideText: 'never',
				},
				disabled: this.isReadonly(),
				customSort: i + 1
			};
		}) ?? [];

		if (list.length) list.unshift({
			uniqueId: 0,
			iconContext: {
				iconClass: '',
				iconStyle: 'fa-solid',
				text: '',
				hideText: 'never',
			},
			disabled: this.isReadonly(),
			customSort: 0,
		});

		return list;
	});


	ngOnInit(): void {
		const finalEventId = this.dialog.data?.finalEventId;
		if (finalEventId) {
			this.selectedEventId.set(finalEventId || undefined);
			const event = this.events().find(e => e.uniqueId == finalEventId);
			this.selectedItem.set(event);
		}
	}


	onViewDroplistChange(selectedItem: DroplistItem | undefined) {
		const oldSelectedEventId = this.dialog.data?.finalEventId || undefined;
		this.selectedEventId.set(selectedItem?.uniqueId || undefined);
		this.saveActionEnabled.set(oldSelectedEventId != this.selectedEventId());
	}

	public async setFinalEventId() {
		const accVentureId = this.dialog.data?.accVentureId;
		if (accVentureId) await this.accAreaService.setVentureFinalEventId(accVentureId, this.selectedEventId());
	}

}
